/** Days after past_due_since before hard-locking the dashboard. */
export const BILLING_GRACE_DAYS = 3;

type SubscriptionLike = {
  status?: string | null;
  past_due_since?: string | null;
  provider_meta?: {
    grace_expired_at?: string | null;
    [key: string]: unknown;
  } | null;
} | null | undefined;

/**
 * True when the org can no longer use the dashboard except to reactivate billing.
 *
 * Triggers:
 * - `unpaid` (grace already applied server-side)
 * - `provider_meta.grace_expired_at` set while still past_due/unpaid
 *   (covers Stripe re-sync that leaves status as past_due)
 * - `past_due` past the 3-day grace window
 *
 * Never locks when Stripe reports active/trialing — stale grace_expired_at
 * from a previous cycle must not keep the dashboard locked after recovery.
 */
export function isBillingHardLocked(subscription: SubscriptionLike): boolean {
  if (!subscription) return false;

  const status = subscription.status || '';
  if (status === 'active' || status === 'trialing') return false;
  if (status === 'unpaid') return true;

  if (subscription.provider_meta?.grace_expired_at) return true;

  if (status === 'past_due' && subscription.past_due_since) {
    const since = new Date(subscription.past_due_since).getTime();
    if (Number.isNaN(since)) return false;
    const deadline = since + BILLING_GRACE_DAYS * 24 * 60 * 60 * 1000;
    return Date.now() >= deadline;
  }

  return false;
}
